/**
 * How the text cards of one paragraph relate to each other, before and after an
 * apply, all read off the words the cards change.
 *
 * A text card says what a paragraph read when it was proposed (`textWhenProposed`)
 * and what it should read (`textProposed`). The diff between the two is the
 * card's change, a list of word runs it replaces, deletes or inserts. Laying the
 * runs of several cards on one common text answers three questions with no model:
 *
 *   - before an apply: which pending cards may collide, and how closely
 *     (`potentialCollisionsOfCards`), so each card can say so before the user
 *     applies it;
 *   - during Apply All: where on the paragraph the cards truly collide
 *     (`conflictZonesOfParagraph`), so the model is asked only about those zones,
 *     one zone at a time in reading order, and everything else lands as written
 *     (`composeParagraphText`);
 *   - after any apply: whether each card's change is in the paragraph as it now
 *     reads, whole, in part or not at all (`cardLandingOnParagraphText`), so a
 *     card is never reported applied when another card's words stand in its place.
 *
 * WHY the redline diff's units
 *   Every comparison is made in the units the engine's diff compares
 *   (`WordTokenizer.diffUnitsWithTrailingSeparators`), and two units are the same
 *   word when their `WordTokenizer.comparisonKey` is. A Japanese, Chinese or Thai
 *   sentence has no spaces, so cut at whitespace it was one word: any two cards on
 *   it changed "the same words", Apply All asked the model to choose between them
 *   and wrote one, and the landing check read the other as not applied.
 *
 * WHY characters and not words
 *   Every text this module writes (a zone's readings, a settled zone, the composed
 *   paragraph) is made of the characters the page and the cards hold: a card's
 *   change carries its own text, spacing included, laid on the page by the engine's
 *   own plan (`TextEditPlanner.editsBetween`), not a second copy of it. Words are
 *   never joined back with " ", so a line break, a tab or a non-breaking space a
 *   card wrote is written, and no space is put between two Japanese words.
 */
import type { PlannedEdit } from "../paragraph-splicer";
import { type PlannedTextEdit, TextEditPlanner } from "../text-edit-planner";
import type { RedlineTextParagraph, RedlineTextProposal } from "../wire/redline-payload";
import { threeWayMerge } from "./three-way-merge";
import { wordDiff } from "./word-diff";
import { type DiffUnitWithTrailingSeparator, WordTokenizer } from "./word-tokenizer";

/** The two texts of one card paragraph, the only fields the collision reading needs. */
export interface CardParagraphTexts {
    textWhenProposed: string;
    textProposed: string;
}

/**
 * The units of a text every collision reading compares: the redline diff's units
 * with their offsets and the whitespace after each.
 *
 * EXAMPLE
 *   "本製品の保証期間は一年とする。" -> 本 / 製品 / の / 保証 / 期間 / は / 一年 / と / する / 。
 *   "pay within thirty (30) days"    -> pay / within / thirty / (30) / days
 */
const unitsOf = (text: string): DiffUnitWithTrailingSeparator[] => WordTokenizer.diffUnitsWithTrailingSeparators(text);

/** The texts of the units, in order. */
const unitTextsOf = (units: DiffUnitWithTrailingSeparator[]): string[] => units.map((unit) => unit.text);

/** The key each unit is compared by (`WordTokenizer.comparisonKey`): two units are the same word when their keys are equal. */
const comparisonKeysOf = (unitTexts: string[]): string[] => unitTexts.map((unitText) => WordTokenizer.comparisonKey(unitText));

/**
 * True when two texts read the same once spacing is set aside: the engine's own
 * test for an edit that only changes spacing (`TextEditPlanner.requestWritesNothing`),
 * for which it writes nothing.
 *
 * It is also the one test of "the same change" that the collision notice
 * (`runsMakeTheSameChange`) and Apply All's settlement of a zone (`settledZoneText`)
 * both make, on the text each card writes, so a pair the notice calls identical is a
 * pair Apply All writes once.
 *
 * WHY the characters written, spacing aside
 *   Two cards that each add ", and shall provide … upon request" read as the same units,
 *   but one wrote it after the sentence's full stop and the other in its place
 *   ("Services., and" against "Services, and"): the notice called them identical while
 *   Apply All asked the model to choose (R25, 2026-10-16, 10.3 and 10.18). Compared
 *   character for character instead, two cards that differ only by a double space were
 *   a choice to the notice and one change to Apply All, which reads its zone's readings
 *   trimmed (R25 review); spacing alone is no change to the engine either.
 *
 * EXAMPLE
 *   "Main Street London"   "Main Street\nLondon"   -> true: only the spacing differs
 *   "本製品の"             "本商品の"              -> false
 *   "café" composed        "cafe" + U+0301         -> true: one word in its two Unicode spellings
 *   ", and … request."     ", and … request"       -> false
 */
export const readsAsTheSameWords = (leftText: string, rightText: string): boolean => TextEditPlanner.requestWritesNothing(leftText, rightText);

/** A half-open run of units of the text a change is laid on: `[startWordIndex, endWordIndex)`. */
export interface CardWordSpan {
    startWordIndex: number;
    endWordIndex: number;
}

/**
 * One run of words a card changes, as half-open unit indexes into the text the run
 * is laid on, the units the card puts there, and the characters it writes: the
 * stretch `[startOffset, endOffset)` of that text and the card's own characters
 * written over it, spacing included.
 *
 * CASES
 *   - startWordIndex < endWordIndex, replacementWords empty      -> the card deletes those words
 *   - startWordIndex < endWordIndex, replacementWords not empty  -> the card replaces them
 *   - startWordIndex = endWordIndex, replacementWords not empty  -> the card inserts before that word
 *   - startWordIndex = endWordIndex, replacementWords empty      -> the card changes only the spacing
 *                                                                   before that word, which rides along
 *                                                                   with its word changes
 *                                                                   (`changeWritesOnlySpacing`)
 *
 * EXAMPLE
 *   text "pay within thirty (30) days"   card "pay within sixty (60) days"
 *   -> { startWordIndex: 2, endWordIndex: 4, replacementWords: ["sixty", "(60)"],
 *        startOffset: 11, endOffset: 22, replacementText: "sixty (60)" }
 */
export interface CardWordChange extends CardWordSpan {
    replacementWords: string[];
    startOffset: number;
    endOffset: number;
    replacementText: string;
}

/**
 * How two cards' changes relate on one paragraph, the riskiest first.
 *
 * Apply All must choose between the two cards (a conflict, `proximityNeedsChoice`):
 *   - changes_same_words   -> both change the same location differently: they share a
 *                             changed word, or one adds words inside or right at the edge
 *                             of the words the other changes
 *   - inserts_at_same_spot -> both add words at the same point and change no shared
 *                             word: which goes first, or whether both stay, is open
 *
 * Both land as written (information only):
 *   - edits_nearby         -> they change neighbouring words
 *   - same_sentence        -> they sit apart in the same sentence
 *   - makes_same_change    -> both make the identical change, which lands once
 *
 * EXAMPLE
 *   text "consent, not to be unreasonably withheld, and remains"
 *   card 6 adds "conditioned or delayed," after "withheld,"
 *   card 7 adds "which Customer may withhold in its sole discretion," after "withheld,"
 *   -> inserts_at_same_spot: neither changes a word of the other
 */
export type CollisionProximity =
    | "changes_same_words"
    | "inserts_at_same_spot"
    | "edits_nearby"
    | "same_sentence"
    | "makes_same_change";

/**
 * The order relations are ranked in, riskiest first, so a card pair is tagged by
 * its riskiest relation whatever else the two cards share.
 *
 * EXAMPLE
 *   cards change the middle word the same way, one adds at the sentence's start, the
 *   other at its end -> relations {makes_same_change, same_sentence} -> same_sentence
 */
const PROXIMITY_RANK: CollisionProximity[] = [
    "changes_same_words",
    "inserts_at_same_spot",
    "edits_nearby",
    "same_sentence",
    "makes_same_change",
];

/** True when Apply All has to choose between the two cards: the relation puts both on words only one reading can hold. */
export const proximityNeedsChoice = (proximity: CollisionProximity): boolean =>
    proximity === "changes_same_words"
    || proximity === "inserts_at_same_spot";

/**
 * True when two runs make the same change: they cover the same characters of the text
 * both are laid on and write the same characters over them, spacing aside
 * (`readsAsTheSameWords`).
 *
 * EXAMPLE, on "…required to perform the Services."
 *   [100,100) ", and shall … request"  and [100,100) ", and shall … request"   -> true
 *   [101,101) ", and shall … request." and [100,100) ", and shall … request"   -> false:
 *     the first keeps the full stop before its words
 */
function runsMakeTheSameChange(firstChange: CardWordChange, secondChange: CardWordChange): boolean {
    return firstChange.startOffset === secondChange.startOffset
        && firstChange.endOffset === secondChange.endOffset
        && readsAsTheSameWords(firstChange.replacementText, secondChange.replacementText);
}

/**
 * The relation of one run of a card to one run of another.
 *
 * CASES, checked in this order
 *   - the same characters written over the
 *     same characters (`runsMakeTheSameChange`) -> makes_same_change
 *   - both insert at one point                -> inserts_at_same_spot
 *   - the runs touch the same location: covers
 *     share a word, or one inserts inside or
 *     right at an edge of the other's cover     -> changes_same_words
 *   - the covers meet                         -> edits_nearby
 *   - both in one sentence                    -> same_sentence
 *
 * NULL
 *   the runs sit in different sentences.
 *
 * EXAMPLE
 *   [2,4) "sixty (60)"  and [2,4) "ninety (90)"     -> changes_same_words
 *   [4,4) "written"     and [4,4) "prior"           -> inserts_at_same_spot
 *   [6,6) "net"         and [4,6) "one month"       -> changes_same_words: "net" lands at the very location the other rewrites
 *   [6,8) "twelve (12)" and [8,9) "business days"   -> edits_nearby
 *   [1,2) 製品→商品     and [6,7) 一年→二年          -> same_sentence: 本製品の保証期間は一年とする。
 */
function proximityOfChanges(firstChange: CardWordChange, secondChange: CardWordChange, sentenceIndexOfWord: number[]): CollisionProximity | null {
    if (runsMakeTheSameChange(firstChange, secondChange)) return "makes_same_change";
    if (isInsertion(firstChange) && isInsertion(secondChange) && firstChange.startWordIndex === secondChange.startWordIndex) return "inserts_at_same_spot";
    if (changesTouchSameWords(firstChange, secondChange)) return "changes_same_words";
    if (changesMeetAtAnEdge(firstChange, secondChange)) return "edits_nearby";
    const firstSentences = sentencesOfChange(firstChange, sentenceIndexOfWord);
    return [...sentencesOfChange(secondChange, sentenceIndexOfWord)].some((sentenceIndex) => firstSentences.has(sentenceIndex)) ? "same_sentence" : null;
}

/**
 * One planner "words" edit as a card's change: the units it names on each side,
 * the characters it covers, and the card's words for them.
 *
 * WHY one edit is one change
 *   The planner writes one edit per run of units it changes, and a change to the
 *   punctuation at a word's end is its own edit over that one word
 *   (`TextEditPlanner.editsBetween`), so no two edits ever share a run.
 *
 * EXAMPLE
 *   base "at its office, and (b)"   card "at its office; and. (b)"
 *   planner: "," -> ";" over the unit [2,3) and "" -> "." over the unit [3,4)
 *   -> two changes, { startWordIndex 2, …, replacementText ";" } and
 *      { startWordIndex 3, …, replacementText "." }
 */
function wordChangeOfPlannedEdit(wordEdit: PlannedTextEdit, cardUnitTexts: string[]): CardWordChange {
    return {
        startWordIndex: wordEdit.sourceUnitStart,
        endWordIndex: wordEdit.sourceUnitEnd,
        /** `requestUnitStart..requestUnitEnd` is half-open, so the slice takes exactly the edit's units of the card's text. */
        replacementWords: cardUnitTexts.slice(wordEdit.requestUnitStart, wordEdit.requestUnitEnd),
        startOffset: wordEdit.startOffset,
        endOffset: wordEdit.endOffset,
        replacementText: wordEdit.replacementText,
    };
}

/** A card's changes on a text: the runs of words it changes, and the spacing it changes between words it keeps. */
interface CardChangesOnText {
    wordChanges: CardWordChange[];
    spacingChanges: CardWordChange[];
}

/**
 * Every change of `cardParagraph` laid on `baseText`, as the engine would write it.
 *
 * CASES, for the card's text on the base
 *   - the card was proposed against `baseText`   -> its own `textProposed`
 *   - it was proposed against another text        -> the card's change carried onto `baseText`
 *                                                   by the three-way merge
 *
 * CASES, for what is written: the engine's own plan of the card's text over the base
 * (`TextEditPlanner.editsBetween`)
 *   - the card's text reads as the base once spacing is set aside -> nothing: an edit
 *     that only changes spacing writes nothing, as the engine writes nothing for it
 *   - otherwise -> every run of words the card changes as a word change, and every gap
 *     it changes between words it keeps as a spacing change, so spacing that rides
 *     along with a word change is written, as the engine writes it
 *
 * WHY
 *   Two cards proposed in different turns may have read the paragraph differently
 *   (the user typed in between). Their runs only compare when they index the same
 *   units, so the later card's words are always read on the one text both are
 *   compared on.
 */
function changesOfCardTextOnBase(cardParagraph: CardParagraphTexts, baseText: string): CardChangesOnText {
    const cardTextOnBase = readsAsTheSameWords(cardParagraph.textWhenProposed, baseText)
        ? cardParagraph.textProposed
        /** The base is always a reading of the document (the page, or the text the cards were proposed on), never another pending card's words. */
        : threeWayMerge({ textWhenProposed: cardParagraph.textWhenProposed, textProposed: cardParagraph.textProposed, textOnPage: baseText, textOnPageIsTheDocumentsOwn: true });
    const plannedEdits = TextEditPlanner.editsBetween(baseText, cardTextOnBase);
    const cardUnitTexts = unitTextsOf(unitsOf(cardTextOnBase));
    const wordChanges = plannedEdits.filter((plannedEdit) => plannedEdit.kind === "words").map((wordEdit) => wordChangeOfPlannedEdit(wordEdit, cardUnitTexts));
    const spacingChanges = plannedEdits.filter((plannedEdit) => plannedEdit.kind === "spacing").map((spacingEdit): CardWordChange => ({
        /** The planner names a spacing edit by the empty run at the unit after the gap: a point that covers and writes no unit. */
        startWordIndex: spacingEdit.sourceUnitStart,
        endWordIndex: spacingEdit.sourceUnitEnd,
        replacementWords: [],
        startOffset: spacingEdit.startOffset,
        endOffset: spacingEdit.endOffset,
        replacementText: spacingEdit.replacementText,
    }));
    return { wordChanges, spacingChanges };
}

/**
 * The runs of words `cardParagraph` changes, laid on `baseText`
 * (`changesOfCardTextOnBase`), without the spacing that rides along with them: the
 * runs the collision reading compares.
 *
 * EXAMPLE
 *   base "本製品の保証期間は一年とする。"   card "本商品の保証期間は一年とする。"
 *   -> [{ startWordIndex: 1, endWordIndex: 2, replacementWords: ["商品"], startOffset: 1, endOffset: 3, replacementText: "商品" }]
 */
export function wordChangesOfCardOnText(cardParagraph: CardParagraphTexts, baseText: string): CardWordChange[] {
    return changesOfCardTextOnBase(cardParagraph, baseText).wordChanges;
}

/**
 * Every change `cardParagraph` writes on `baseText`: its runs of words, then the
 * spacing it changes between words it keeps (`changeWritesOnlySpacing`), which the
 * zoning hands to the composer as uncontested so it is written with the card's
 * words.
 *
 * EXAMPLE
 *   base "Main Street London EC1A 1AA"   card "Main Street\nLondon EC1A 1BB"
 *   -> the run "1AA" -> "1BB", and the gap after "Street" -> "\n"
 */
export function changesOfCardOnText(cardParagraph: CardParagraphTexts, baseText: string): CardWordChange[] {
    const { wordChanges, spacingChanges } = changesOfCardTextOnBase(cardParagraph, baseText);
    return [...wordChanges, ...spacingChanges];
}

/** True when the run inserts words at a point and covers none of the text's words. */
const isInsertion = (change: CardWordSpan): boolean => change.startWordIndex === change.endWordIndex;

/** True when the change covers no word and writes none: a change of the spacing between two words the card keeps. */
export const changeWritesOnlySpacing = (change: CardWordChange): boolean => isInsertion(change) && change.replacementWords.length === 0;

/**
 * True when one card's run and another's cannot both be written as they are: they
 * cover a shared word, one inserts inside or right against the other's words, or
 * both insert at the same point.
 *
 * CASES
 *   - both cover words, the covers intersect                  -> true   [2,4) and [3,5)
 *   - an insertion strictly inside the other's cover          -> true   [3,3) inside [2,4)
 *   - an insertion at either edge of the other's cover        -> true   [4,4) against [2,4): the words
 *                                                                      inserted run straight on from
 *                                                                      "withheld," which the other card
 *                                                                      rewrote
 *   - both insert at the same point                           -> true   [3,3) and [3,3)
 *   - covers that only meet at an edge                        -> false  [2,4) and [4,6): different words
 *
 * EXAMPLE
 *   text   "consent, not to be unreasonably withheld, and remains"
 *   card a "not to be unreasonably withheld,"  -> "which Customer may withhold in its sole discretion,"   [1,6)
 *   card b inserts "conditioned or delayed," before "and"                                              [6,6)
 *   -> true: b's words would follow a's rewrite and read "discretion, conditioned or delayed,"
 */
export function changesTouchSameWords(first: CardWordSpan, second: CardWordSpan): boolean {
    if (isInsertion(first) && isInsertion(second)) return first.startWordIndex === second.startWordIndex;
    /** An insertion point at `startWordIndex` touches a cover [start, end) when it sits anywhere from its first word's front to its last word's back, both edges included. */
    if (isInsertion(first)) return first.startWordIndex >= second.startWordIndex && first.startWordIndex <= second.endWordIndex;
    if (isInsertion(second)) return second.startWordIndex >= first.startWordIndex && second.startWordIndex <= first.endWordIndex;
    return first.startWordIndex < second.endWordIndex && second.startWordIndex < first.endWordIndex;
}

/**
 * True when two covering runs change neighbouring words: one's last word is right
 * before the other's first.
 *
 * EXAMPLE
 *   [2,4) and [4,6) -> true: "twenty-four (24)" and "hours" are next to each other
 */
const changesMeetAtAnEdge = (first: CardWordChange, second: CardWordChange): boolean =>
    first.endWordIndex === second.startWordIndex || second.endWordIndex === first.startWordIndex;

/**
 * How many words a sentence needs before a full stop ends it. Two words and a
 * stop ("1.2 Services.") are a heading or a number, not a sentence.
 */
const MINIMUM_WORDS_PER_SENTENCE = 3;

/**
 * The sentence each word belongs to, numbered from 0.
 *
 * WHY
 *   The rule is a heuristic on purpose: a sentence is at least three words ending
 *   in a stop. It never splits inside "1.2" (no stop at the word's end) and does not
 *   end a sentence on a clause number standing alone.
 *
 * EXAMPLE
 *   words ["1.1", "Customer", "shall", "pay.", "Late", "amounts", "bear", "interest."]
 *   -> [0, 0, 0, 0, 1, 1, 1, 1]
 *   units ["修理", "は", "無償", "と", "する", "。", "本", "製品"]
 *   -> [0, 0, 0, 0, 0, 0, 1, 1]
 */
export function sentenceIndexesOfWords(words: string[]): number[] {
    const sentenceIndexOfWord: number[] = [];
    let currentSentenceIndex = 0;
    let wordsInCurrentSentence = 0;
    for (const word of words) {
        sentenceIndexOfWord.push(currentSentenceIndex);
        wordsInCurrentSentence += 1;
        /**
         * WHEN the word closes a sentence long enough to be one: its last marks end a
         *      sentence or a clause closed by a semicolon, read through the fold
         *      (`WordTokenizer.endsSentenceOrSemicolonClause`: "pay." "interest;" "。"
         *      "？" "とする。」" "Tage.“")
         *   CONSEQUENCE
         *     the next word starts the next sentence.
         */
        if (WordTokenizer.endsSentenceOrSemicolonClause(word) && wordsInCurrentSentence >= MINIMUM_WORDS_PER_SENTENCE) {
            currentSentenceIndex += 1;
            wordsInCurrentSentence = 0;
        }
    }
    return sentenceIndexOfWord;
}

/**
 * The sentences a run sits in.
 *
 * CASES
 *   - it covers words              -> the sentences of those words
 *   - it inserts after a word      -> the sentence of the word before the point, which the inserted words continue
 *   - it inserts at the very start -> the first sentence
 *
 * EXAMPLE
 *   sentenceIndexOfWord [0, 0, 0, 1, 1]
 *   cover [2,4)    -> {0, 1}
 *   insertion [3,3) -> {0}: word 2 is the word before the point
 */
function sentencesOfChange(change: CardWordChange, sentenceIndexOfWord: number[]): Set<number> {
    if (isInsertion(change)) {
        /** The word before an insertion point at `startWordIndex` is `startWordIndex - 1`; at point 0 there is none, and the first sentence is taken. */
        const wordBeforePoint = Math.max(0, change.startWordIndex - 1);
        return new Set([sentenceIndexOfWord[wordBeforePoint] ?? 0]);
    }
    return new Set(sentenceIndexOfWord.slice(change.startWordIndex, change.endWordIndex));
}

/**
 * Every relation between any run of one card and any run of another, on one text,
 * so a pair that both repeats a change and conflicts elsewhere keeps both facts.
 *
 * EMPTY when no run of one card is in the same sentence as any run of the other.
 *
 * EXAMPLE
 *   card a: adds "Promptly," at the start, changes "thirty (30)" to "forty-five (45)"
 *   card b: changes "thirty (30)" to "forty-five (45)", adds "in full" at the end
 *   -> {makes_same_change, same_sentence}
 */
export function relationsBetweenChanges(firstChanges: CardWordChange[], secondChanges: CardWordChange[], sentenceIndexOfWord: number[]): Set<CollisionProximity> {
    const relations = new Set<CollisionProximity>();
    for (const firstChange of firstChanges) {
        for (const secondChange of secondChanges) {
            const proximity = proximityOfChanges(firstChange, secondChange, sentenceIndexOfWord);
            /** Runs in different sentences add no relation. */
            if (proximity !== null) relations.add(proximity);
        }
    }
    return relations;
}

/**
 * The riskiest of a pair's relations (`PROXIMITY_RANK`).
 *
 * NULL
 *   the pair has no relation.
 */
const riskiestRelationOf = (relations: Set<CollisionProximity>): CollisionProximity | null =>
    PROXIMITY_RANK.find((proximity) => relations.has(proximity)) ?? null;

/**
 * One card paragraph as the collision readings take it (`collisionParagraphsOf`):
 * the paragraph as the card carries it, and whether it is a cell of a row the card
 * deletes.
 *
 * NULL
 *   `inRowCardDeletes` is left out by a reader that never strikes a row (the landing
 *   audit, a card with no row deletion): the paragraph is then read as an edit.
 */
export interface CollisionParagraph extends RedlineTextParagraph {
    inRowCardDeletes?: boolean;
}

/** One pending card as the collision reading sees it: an id, and its paragraphs by bookmark, as the card carries them. */
export interface CollisionCard {
    cardId: string;
    paragraphs: CollisionParagraph[];
}

/**
 * The row a cell's paragraph sits in, as one reader keys it, or null where that
 * reader cannot tell: Apply All passes the page it read (`"table:row"`).
 */
export type RowKeyOfBookmark = (bookmark: string) => string | null;

/**
 * The card's paragraphs as every collision reading takes them: the chat's notice
 * before an apply, the tracker's conflict groups, and Apply All's zoning.
 *
 * CASES, for a card that deletes a row, per cell it lists
 *   - a deletion names the cell                                -> emptied, so the deletion
 *                                                               collides with any edit there
 *   - `rowKeyOfBookmark` given (Apply All, from its page)      -> emptied when it places the cell
 *                                                               in a deleted row; as the card
 *                                                               carries it otherwise, rows it
 *                                                               cannot place included
 *   - no `rowKeyOfBookmark` (the notice and the tracker, before
 *     any page is read)                                        -> emptied when the card lists the
 *                                                               cell unchanged: the core lists a
 *                                                               struck row's cells as they read,
 *                                                               and a cell the card edits is in
 *                                                               a row it keeps
 *   - a card that only adds rows or a table                    -> its anchor cells as it carries
 *                                                               them, unchanged: it collides
 *                                                               with nothing
 *
 * WHY only the cells of rows the card deletes
 *   A card that merges two rows edits the first and deletes the second, and the core lists
 *   the cells of both (`section_with_struck_rows`). Read as emptied across every cell it
 *   listed, Apply All wrote the kept row's merged question as nothing and the row went with
 *   the other (R27, 2026-10-16: two merges in one table lost both rows each), and the
 *   chat's notice read the kept row as deleted too.
 *
 * WHY Apply All reads rows and the notice reads the card
 *   Apply All writes what it reads: a cell it empties is struck in the document, so it
 *   empties only a cell its page places in a deleted row, and a cell of a row it cannot
 *   place stays as the card writes it. The notice writes nothing, and reads an unchanged
 *   cell of a merge card's kept row as emptied at worst, a notice of a collision
 *   Apply All then never writes.
 *
 * EXAMPLE
 *   card deletes the Premium support row, naming its first cell; it lists the row's three
 *   cells, which the page places in that row
 *   -> [{ "Premium support" -> "" }, { "per year" -> "" }, { "18% of the subscription fee" -> "" }]
 *
 *   card edits row 1's question and deletes row 2, naming row 2's first cell
 *   -> row 1's question as the card writes it; row 2's cells -> ""
 *
 * Each emptied cell is marked `inRowCardDeletes`, so a cell already empty, which the
 * card's texts alone read as unchanged, is still read as struck (R31).
 */
export function collisionParagraphsOf(card: RedlineTextProposal, rowKeyOfBookmark: RowKeyOfBookmark | null = null): CollisionParagraph[] {
    if (card.tableChanges.rowDeletions.length === 0) return card.paragraphs;
    const cellsDeletionsName = new Set(card.tableChanges.rowDeletions.map((deletion) => deletion.rowOfBookmark));
    const deletedRowKeys = new Set(card.tableChanges.rowDeletions.flatMap((deletion) => {
        const rowKey = rowKeyOfBookmark === null ? null : rowKeyOfBookmark(deletion.rowOfBookmark);
        return rowKey === null ? [] : [rowKey];
    }));
    /** The CASES above, for one listed cell. */
    const isCellOfDeletedRow = (paragraph: RedlineTextProposal["paragraphs"][number]): boolean => {
        if (paragraph.bookmark === null) return false;
        if (cellsDeletionsName.has(paragraph.bookmark)) return true;
        if (rowKeyOfBookmark === null) return paragraph.textProposed === paragraph.textWhenProposed;
        const rowKey = rowKeyOfBookmark(paragraph.bookmark);
        return rowKey !== null && deletedRowKeys.has(rowKey);
    };
    return card.paragraphs.map((paragraph) => (isCellOfDeletedRow(paragraph) ? { ...paragraph, textProposed: "", inRowCardDeletes: true } : paragraph));
}

/**
 * One card's relation to another card: the riskiest relation found on any
 * paragraph both change, and whether the two also make one identical change
 * somewhere, which a riskier relation would otherwise hide.
 */
export interface PotentialCollision {
    cardId: string;
    otherCardId: string;
    proximity: CollisionProximity;
    alsoMakesSameChange: boolean;
}

/** True when the card paragraph changes any word: its texts do not read the same once spacing is set aside. */
const changesAnyWord = (cardParagraph: CardParagraphTexts): boolean => !readsAsTheSameWords(cardParagraph.textWhenProposed, cardParagraph.textProposed);

/** True when the card paragraph is a cell of a row the card deletes (`collisionParagraphsOf`). */
const strikesRowOfParagraph = (cardParagraph: CollisionParagraph): boolean => cardParagraph.inRowCardDeletes === true;

/**
 * True when the card paragraph takes part in a collision reading: it changes a word,
 * or its card strikes its row, which an empty cell's texts alone never show.
 */
const contestsParagraph = (cardParagraph: CollisionParagraph): boolean => changesAnyWord(cardParagraph) || strikesRowOfParagraph(cardParagraph);

/**
 * The relation of two cards on one cell where one or both strike the cell's row.
 *
 * CASES
 *   - both strike the row                          -> makes_same_change: the row goes once
 *   - one strikes it, the other changes a word     -> changes_same_words: the row cannot both go
 *                                                     and hold the edit, whatever the cell holds
 *   - one strikes it, the other leaves it as it is -> null: no relation
 *
 * WHY a reading of its own
 *   A struck cell that is already empty reads "" before and after, so its texts show no
 *   change and no run of it meets the other card's: an answer written into the empty
 *   cell of a row another card strikes went unnoticed, and Apply All wrote the strike
 *   and lost the answer (R31, 2026-10-16). A cell with words was read by its runs, the
 *   strike's deletion against the edit, which gives the same relation.
 *
 * EXAMPLE
 *   card 3 deletes row 2; card 5 writes "From the signed order form." into row 2's empty answer cell
 *   -> changes_same_words
 */
function struckRowRelationOf(firstParagraph: CollisionParagraph, secondParagraph: CollisionParagraph): CollisionProximity | null {
    if (strikesRowOfParagraph(firstParagraph) && strikesRowOfParagraph(secondParagraph)) return "makes_same_change";
    const editingParagraph = strikesRowOfParagraph(firstParagraph) ? secondParagraph : firstParagraph;
    return changesAnyWord(editingParagraph) ? "changes_same_words" : null;
}

/**
 * Every pair of pending cards whose changes sit close on a paragraph both change,
 * listed once from each card's side, with the closest proximity.
 *
 * Both cards' runs are laid on the first card's `textWhenProposed` for that
 * paragraph (`wordChangesOfCardOnText`), so cards proposed against different
 * readings of the paragraph still compare word for word.
 *
 * EXAMPLE
 *   card 1 changes "thirty (30)" to "forty-five (45)", card 2 to "ninety (90)", same paragraph
 *   -> [{card 1, card 2, changes_same_words}, {card 2, card 1, changes_same_words}]
 */
export function potentialCollisionsOfCards(cards: CollisionCard[]): PotentialCollision[] {
    const collisions: PotentialCollision[] = [];
    cards.forEach((firstCard, firstCardIndex) => {
        /** Pairs are taken once, the second card after the first in the list, and listed from both sides below. */
        cards.slice(firstCardIndex + 1).forEach((secondCard) => {
            const relations = new Set<CollisionProximity>();
            for (const firstParagraph of firstCard.paragraphs) {
                /** A paragraph with no bookmark cannot be matched to another card's paragraph. */
                if (!firstParagraph.bookmark || !contestsParagraph(firstParagraph)) continue;
                const secondParagraph = secondCard.paragraphs.find((candidate) => candidate.bookmark === firstParagraph.bookmark);
                if (secondParagraph === undefined || !contestsParagraph(secondParagraph)) continue;
                /** WHEN either card strikes the cell's row CONSEQUENCE the row's relation stands for the cell's runs (`struckRowRelationOf`). */
                if (strikesRowOfParagraph(firstParagraph) || strikesRowOfParagraph(secondParagraph)) {
                    const struckRowRelation = struckRowRelationOf(firstParagraph, secondParagraph);
                    if (struckRowRelation !== null) relations.add(struckRowRelation);
                    continue;
                }
                const baseText = firstParagraph.textWhenProposed;
                relationsBetweenChanges(
                    wordChangesOfCardOnText(firstParagraph, baseText),
                    wordChangesOfCardOnText(secondParagraph, baseText),
                    sentenceIndexesOfWords(unitTextsOf(unitsOf(baseText))),
                ).forEach((relation) => relations.add(relation));
            }
            const proximity = riskiestRelationOf(relations);
            /** A pair with no relation on any shared paragraph is not listed. */
            if (proximity === null) return;
            const alsoMakesSameChange = proximity !== "makes_same_change" && relations.has("makes_same_change");
            collisions.push({ cardId: firstCard.cardId, otherCardId: secondCard.cardId, proximity, alsoMakesSameChange });
            collisions.push({ cardId: secondCard.cardId, otherCardId: firstCard.cardId, proximity, alsoMakesSameChange });
        });
    });
    return collisions;
}

/** One card's changes on a paragraph, as the zoning reads them: its runs of words, and the spacing that rides along with them where the caller composes the paragraph (`changesOfCardOnText`). */
export interface CardChangesOnParagraph {
    cardId: string;
    changes: CardWordChange[];
}

/**
 * One card's reading of a conflict zone: the zone's words with that card's runs in
 * the zone written (`optionText`, what the model and the user are shown), and the
 * characters the card writes over the zone's stretch of the paragraph
 * (`writtenText`, the same words with the spacing the card wrote at their edges).
 *
 * EXAMPLE
 *   zone "\n" between "Notices:" and "Acme"; the card writes "Notices: Attention Legal\nAcme"
 *   -> { optionText: "Attention Legal", writtenText: " Attention Legal\n" }
 *
 * `removesRow` marks the reading of a card that strikes the cell's row
 * (`struckCellZoneOf`): it reads "" and is shown as the row's removal.
 *
 * NULL
 *   `removesRow` is left out on every other reading.
 */
export interface ConflictZoneOption {
    cardId: string;
    optionText: string;
    writtenText: string;
    removesRow?: boolean;
}

/**
 * A stretch of the paragraph where two or more cards' runs touch the same words:
 * its half-open unit span on the paragraph, the characters it covers
 * (`[startOffset, endOffset)`: every character any of its runs writes over), the
 * words it holds today, and each card's reading of it, in card order.
 *
 * EXAMPLE
 *   text "pay within thirty (30) days", cards 45 and 90
 *   -> { zoneStartWordIndex: 2, zoneEndWordIndex: 4, startOffset: 11, endOffset: 22,
 *        originalText: "thirty (30)",
 *        options: [{ card 45, "forty-five (45)" }, { card 90, "ninety (90)" }] }
 */
export interface ConflictZone {
    zoneStartWordIndex: number;
    zoneEndWordIndex: number;
    startOffset: number;
    endOffset: number;
    originalText: string;
    options: ConflictZoneOption[];
}

/** A card's change that touches no other card's run: it lands as the card wrote it. A change of spacing alone is written only with its card's words (`composeParagraphText`). */
export interface UncontestedCardChange {
    cardId: string;
    change: CardWordChange;
}

/** A paragraph read as the changes that land as written and the zones a choice is needed for, both in reading order. */
export interface ParagraphZoning {
    uncontestedChanges: UncontestedCardChange[];
    zones: ConflictZone[];
}

/** One card's run with the card it belongs to, while zones are grown. */
interface OwnedCardChange {
    cardId: string;
    change: CardWordChange;
}

/**
 * The characters of `text[startOffset, endOffset)` with `edits` written over them.
 * The edits are of one card, or are kept apart by the caller, so none overlaps
 * another; each lies inside the stretch.
 *
 * EXAMPLE
 *   text "within thirty (30) days", stretch 7..18 "thirty (30)",
 *   edit 7..18 -> "sixty (60)"
 *   -> "sixty (60)"
 */
function stretchWithEdits(text: string, startOffset: number, endOffset: number, edits: PlannedEdit[]): string {
    let written = "";
    let cursor = startOffset;
    for (const edit of [...edits].sort((left, right) => left.startOffset - right.startOffset || left.endOffset - right.endOffset)) {
        /** The characters from the cursor up to the edit's start stand as the text has them; the edit then moves the cursor to its end. */
        written += text.slice(cursor, edit.startOffset) + edit.replacementText;
        cursor = edit.endOffset;
    }
    return written + text.slice(cursor, endOffset);
}

/**
 * The paragraph `baseText` read as the cards' changes that land untouched and the
 * zones where runs of different cards touch the same words
 * (`changesTouchSameWords`), in reading order.
 *
 * Zones grow transitively: a run that touches a zone's run joins that zone, and two
 * zones one run touches become one. A change of spacing alone touches nothing and
 * is always uncontested.
 *
 * EXAMPLE
 *   text "Supplier shall promptly notify Customer"
 *   card a deletes "promptly"                      [2,3)
 *   card b replaces "promptly" with "without undue delay" [2,3)
 *   card c inserts "in writing" before "Customer"  [4,4)
 *   -> uncontested [c], zones [{ [2,3), "promptly", options a "", b "without undue delay" }]
 *
 * `cardIdsStrikingParagraph` names the cards that strike the row the paragraph is a
 * cell of (`collisionParagraphsOf`); a cell struck by one card and changed by another
 * is one zone over the whole cell (`struckCellZoneOf`).
 */
export function conflictZonesOfParagraph(baseText: string, cardsChanges: CardChangesOnParagraph[], cardIdsStrikingParagraph: ReadonlySet<string> = new Set()): ParagraphZoning {
    const struckCellZone = struckCellZoneOf(baseText, cardsChanges, cardIdsStrikingParagraph);
    if (struckCellZone !== null) return { uncontestedChanges: [], zones: [struckCellZone] };
    const baseUnits = unitsOf(baseText);
    const ownedChanges: OwnedCardChange[] = cardsChanges.flatMap((cardChanges) => cardChanges.changes.map((change) => ({ cardId: cardChanges.cardId, change })));
    let changeGroups: OwnedCardChange[][] = [];
    for (const ownedChange of ownedChanges.filter((owned) => !changeWritesOnlySpacing(owned.change))) {
        /** A run joins every group holding another card's run it touches; those groups merge into one. */
        const touchedGroups = changeGroups.filter((changeGroup) =>
            changeGroup.some((member) => member.cardId !== ownedChange.cardId && changesTouchSameWords(member.change, ownedChange.change)));
        changeGroups = [...changeGroups.filter((changeGroup) => !touchedGroups.includes(changeGroup)), [...touchedGroups.flat(), ownedChange]];
    }
    const uncontestedChanges: UncontestedCardChange[] = ownedChanges.filter((owned) => changeWritesOnlySpacing(owned.change));
    const zones: ConflictZone[] = [];
    for (const changeGroup of changeGroups) {
        const cardIdsOfGroup = Array.from(new Set(changeGroup.map((member) => member.cardId)));
        /**
         * WHEN every run of the group is one card's
         *   CONSEQUENCE
         *     nothing collides there: the run lands as written.
         */
        if (cardIdsOfGroup.length === 1) {
            uncontestedChanges.push(...changeGroup);
            continue;
        }
        zones.push(conflictZoneOf(baseText, baseUnits, changeGroup, cardsChanges.filter((cardChanges) => cardIdsOfGroup.includes(cardChanges.cardId)).map((cardChanges) => cardChanges.cardId)));
    }
    /** Changes are listed by the word they start at, then by the character, so spacing before a word comes before a run at that word. */
    const inReadingOrder = (left: UncontestedCardChange, right: UncontestedCardChange): number =>
        left.change.startWordIndex - right.change.startWordIndex || left.change.startOffset - right.change.startOffset;
    return {
        uncontestedChanges: uncontestedChanges.sort(inReadingOrder),
        zones: zones.sort((left, right) => left.zoneStartWordIndex - right.zoneStartWordIndex),
    };
}

/**
 * The one zone of a cell whose row a card strikes while another card changes its
 * words: the whole cell, each striking card reading "" marked `removesRow`, and each
 * other card that changes a word reading the cell as it writes it, in card order.
 *
 * NULL
 *   no card strikes the cell's row, or no other card changes a word of it: the cell
 *   is zoned by its runs, and a strike alone goes with its row.
 *
 * WHY the whole cell
 *   A row cannot both go and hold an edit, so a strike and an edit are two readings of
 *   the cell however their runs lie. Read by its runs, the strike of an empty cell had
 *   none: the edit landed uncontested in a row that then went (R31, 2026-10-16). And a
 *   strike against an edit that only deletes words settled as the union of the two
 *   deletions, so the edit lost with no choice made.
 *
 * EXAMPLE
 *   cell     card 3          card 5                          zone
 *   ""       strikes the row "From the signed order form."   words 0..0, offsets 0..0, today ""
 *   "Yes."   strikes the row "Yes, from the order form."     words 0..1, offsets 0..4, today "Yes."
 *   -> options [{ 3, "", removesRow }, { 5, the cell as card 5 writes it }]
 */
function struckCellZoneOf(baseText: string, cardsChanges: CardChangesOnParagraph[], cardIdsStrikingParagraph: ReadonlySet<string>): ConflictZone | null {
    /** A card whose only change on the cell is spacing reads the cell's words as they are: it edits nothing a strike contests. */
    const changesWordsOfCell = (cardChanges: CardChangesOnParagraph): boolean => cardChanges.changes.some((change) => !changeWritesOnlySpacing(change));
    const strikingCards = cardsChanges.filter((cardChanges) => cardIdsStrikingParagraph.has(cardChanges.cardId));
    const editingCards = cardsChanges.filter((cardChanges) => !cardIdsStrikingParagraph.has(cardChanges.cardId) && changesWordsOfCell(cardChanges));
    if (strikingCards.length === 0 || editingCards.length === 0) return null;
    return {
        /** Every unit of the cell: from the first, 0, to one past the last, the unit count ("Yes." has 1: 0..1; an empty cell has none: 0..0). */
        zoneStartWordIndex: 0,
        zoneEndWordIndex: unitsOf(baseText).length,
        /** Every character of the cell, 0 to its length: "Yes." -> 0..4. */
        startOffset: 0,
        endOffset: baseText.length,
        originalText: baseText.trim(),
        options: cardsChanges.flatMap((cardChanges): ConflictZoneOption[] => {
            if (cardIdsStrikingParagraph.has(cardChanges.cardId)) return [{ cardId: cardChanges.cardId, optionText: "", writtenText: "", removesRow: true }];
            /** A card that lists the cell but changes no word of it offers no reading. */
            if (!changesWordsOfCell(cardChanges)) return [];
            const writtenText = stretchWithEdits(baseText, 0, baseText.length, cardChanges.changes);
            return [{ cardId: cardChanges.cardId, optionText: writtenText.trim(), writtenText }];
        }),
    };
}

/**
 * The characters a run's own words stand on in the base text: from its first word's
 * start to its last word's end.
 *
 * NULL
 *   the run covers no word (an insertion): it stands on no character of the base.
 *
 * EXAMPLE
 *   base "at its office, and (b)", run [2,4) "office, and" -> 7..18
 */
function wordsStretchOf(change: CardWordChange, baseUnits: DiffUnitWithTrailingSeparator[]): PlannedEdit | null {
    if (isInsertion(change)) return null;
    /** `endWordIndex - 1` is the run's last word, since the end is exclusive. */
    return { startOffset: baseUnits[change.startWordIndex].startOffset, endOffset: baseUnits[change.endWordIndex - 1].endOffset, replacementText: "" };
}

/**
 * One zone of runs of different cards that touch: its unit span, the characters
 * it covers, the words those characters hold today, and every card's reading of
 * them, in card order.
 *
 * WHY the characters it covers
 *   Each run is laid as the engine lays it (`TextEditPlanner.editsBetween`), so a
 *   run's characters take a space or a line break only where its card changed it.
 *   The zone covers every character any of its runs writes over and every word they
 *   cover, so a change the engine draws on a word's ending punctuation alone
 *   ("office," -> "office;") still shows its whole word in the zone. Every card's reading is that
 *   stretch with the card's runs written in, exactly as the card has them, and the
 *   words shown for it are the reading without the spacing at its edges.
 *
 * EXAMPLE
 *   text "6.4 … on notice to …", cards inserting "written" and "prior" before "notice"
 *   -> stretch 49..49 (the start of "notice"), originalText "",
 *      readings "written " and "prior ", shown as "written" and "prior"
 */
function conflictZoneOf(baseText: string, baseUnits: DiffUnitWithTrailingSeparator[], changeGroup: OwnedCardChange[], cardIdsInOrder: string[]): ConflictZone {
    const stretches = changeGroup.flatMap((member) => {
        const wordsStretch = wordsStretchOf(member.change, baseUnits);
        /** An insertion covers no word: its own characters are its whole stretch. */
        return wordsStretch === null ? [member.change] : [member.change, wordsStretch];
    });
    const startOffset = Math.min(...stretches.map((stretch) => stretch.startOffset));
    const endOffset = Math.max(...stretches.map((stretch) => stretch.endOffset));
    return {
        zoneStartWordIndex: Math.min(...changeGroup.map((member) => member.change.startWordIndex)),
        zoneEndWordIndex: Math.max(...changeGroup.map((member) => member.change.endWordIndex)),
        startOffset,
        endOffset,
        /** A stretch that holds no word today (every card inserts at one point) reads "": only spacing, if anything, stands there. */
        originalText: baseText.slice(startOffset, endOffset).trim(),
        options: cardIdsInOrder.map((cardId) => {
            const writtenText = stretchWithEdits(baseText, startOffset, endOffset, changeGroup.filter((member) => member.cardId === cardId).map((member) => member.change));
            return { cardId, optionText: writtenText.trim(), writtenText };
        }),
    };
}

/**
 * The stretches the edits write over, overlapping ones united.
 *
 * EXAMPLE
 *   [10,20) and [15,30) and [40,45) -> [10,30) and [40,45)
 */
function unitedStretchesOf(edits: PlannedEdit[]): PlannedEdit[] {
    const united: PlannedEdit[] = [];
    for (const edit of [...edits].sort((left, right) => left.startOffset - right.startOffset)) {
        const previous = united[united.length - 1];
        /**
         * CASES
         *   - the edit starts inside the previous stretch -> the stretch grows to the later end
         *   - it starts past it                           -> a stretch of its own
         */
        if (previous !== undefined && edit.startOffset < previous.endOffset) previous.endOffset = Math.max(previous.endOffset, edit.endOffset);
        else united.push({ startOffset: edit.startOffset, endOffset: edit.endOffset, replacementText: "" });
    }
    return united;
}

/**
 * The zone's text when no choice is needed, or null when one is.
 *
 * CASES
 *   - a card strikes the cell's row              -> null: the row goes or the edit stands, which
 *                                                  only a choice says (`struckCellZoneOf`)
 *   - every card reads the zone the same         -> that reading: the same change twice lands once
 *   - every card only deletes words of the zone  -> every word any card deletes is gone: overlapping
 *                                                  deletions are united
 *   - anything else                               -> null: the model is asked
 *
 * EXAMPLE
 *   zone "promptly and fully", card a deletes "promptly and", card b deletes "and fully"
 *   -> "": every word of the zone is deleted by one card or the other
 */
export function settledZoneText(zone: ConflictZone, cardsChanges: CardChangesOnParagraph[], baseText: string): string | null {
    if (zone.options.some((option) => option.removesRow === true)) return null;
    /**
     * WHEN every card writes the same characters over the zone, spacing aside (`readsAsTheSameWords`,
     * the notice's test too) CONSEQUENCE the zone settles on them, the first card's characters written.
     */
    if (zone.options.every((option) => readsAsTheSameWords(option.optionText, zone.options[0].optionText))) return zone.options[0].optionText;
    const changesInZone = cardsChanges
        .filter((cardChanges) => zone.options.some((option) => option.cardId === cardChanges.cardId))
        .flatMap((cardChanges) => cardChanges.changes.filter((change) =>
            !changeWritesOnlySpacing(change) && change.startWordIndex >= zone.zoneStartWordIndex && change.endWordIndex <= zone.zoneEndWordIndex));
    const everyChangeOnlyDeletes = changesInZone.every((change) => change.replacementWords.length === 0 && !isInsertion(change));
    if (!everyChangeOnlyDeletes) return null;
    /** A character survives the union when no card's deletion covers it; the words shown are the survivors without the spacing at their edges. */
    return stretchWithEdits(baseText, zone.startOffset, zone.endOffset, unitedStretchesOf(changesInZone)).trim();
}

/** A zone and the text chosen for it. */
export interface ChosenZoneText {
    zone: ConflictZone;
    chosenText: string;
}

/** The spacing at the start of a text: the characters before its first non-whitespace one. */
const leadingSpacingOf = (text: string): string => text.match(/^\s*/u)?.[0] ?? "";

/** The spacing at the end of a text: the characters after its last non-whitespace one. */
const trailingSpacingOf = (text: string): string => text.match(/\s*$/u)?.[0] ?? "";

/**
 * The characters a zone's words and one space beside them stand on, for a zone
 * chosen to hold nothing that no card's reading spells: what the engine's plan
 * (`TextEditPlanner.editsBetween`) strikes for a card that deletes those words and
 * keeps the space before them, which takes the space after the words, or the one
 * before them at the paragraph's end.
 *
 * CASES
 *   - words follow the zone        -> from its first word's start to the next word's start
 *   - the zone ends the paragraph  -> from the end of the word before it (or its own start
 *                                     when it is the whole paragraph) to its last word's end
 *
 * EXAMPLE
 *   "pay each undisputed invoice", zone "undisputed" -> "undisputed " goes: "pay each invoice"
 *   "pay each invoice promptly",   zone "promptly"   -> " promptly" goes: "pay each invoice"
 */
function emptiedZoneEditOf(baseText: string, zone: ConflictZone): PlannedEdit {
    const baseUnits = unitsOf(baseText);
    /** `zoneEndWordIndex - 1` is the zone's last word; the zone holds at least one, since an insertion-only zone holds "" today and is kept as it is. */
    const lastUnitOfZone = baseUnits[zone.zoneEndWordIndex - 1];
    const firstUnitOfZone = baseUnits[zone.zoneStartWordIndex];
    if (zone.zoneEndWordIndex < baseUnits.length) return { startOffset: firstUnitOfZone.startOffset, endOffset: baseUnits[zone.zoneEndWordIndex].startOffset, replacementText: "" };
    /** At the paragraph's end the space before the zone goes with it: the end of the word before, `zoneStartWordIndex - 1`, when there is one. */
    const startOffset = zone.zoneStartWordIndex > 0 ? baseUnits[zone.zoneStartWordIndex - 1].endOffset : firstUnitOfZone.startOffset;
    return { startOffset, endOffset: lastUnitOfZone.endOffset, replacementText: "" };
}

/**
 * What a chosen zone writes over the paragraph.
 *
 * CASES
 *   - the zone keeps its words today         -> its stretch as the page has it
 *   - a card's reading is chosen             -> that card's characters over the stretch (`writtenText`)
 *   - "" that no reading spells              -> the zone's words and one space beside them go
 *                                               (`emptiedZoneEditOf`)
 *   - other text (readings the model joined,
 *     or a user's own words)                 -> that text between the spacing the first reading with
 *                                               words wrote at its edges, or the page's when none has words
 *
 * The chosen text is read as its words (`chosenZoneWordsOf`): the gap at the zone's edges
 * is the zone's own, so spacing at the chosen text's edges joins it.
 *
 * EXAMPLE
 *   zone "\n" between "Notices:" and "Acme"; readings " Attention Legal\n", " Attention Finance\n"
 *   chosen "Attention Legal and Finance" -> " Attention Legal and Finance\n"
 *   zone after "days " before "."; reading " in full"; chosen " in full" (§57) -> " in full", one space
 */
function zoneEditOf(baseText: string, chosen: ChosenZoneText): PlannedEdit {
    const { zone } = chosen;
    const chosenText = chosenZoneWordsOf(chosen.chosenText);
    /**
     * The zone's characters as the page has them, `[startOffset, endOffset)`.
     *
     * EXAMPLE
     *   "pay within thirty (30) days", zone 11..22 -> "thirty (30)"
     */
    const zoneStretch = baseText.slice(zone.startOffset, zone.endOffset);
    /** WHEN the chosen words are the zone's words today CONSEQUENCE the stretch is written back as it stands: no change. */
    if (chosenText === chosenZoneWordsOf(zone.originalText)) return { startOffset: zone.startOffset, endOffset: zone.endOffset, replacementText: zoneStretch };
    const chosenReading = zone.options.find((option) => chosenZoneWordsOf(option.optionText) === chosenText);
    /** WHEN a card's reading is the chosen words CONSEQUENCE that card's characters, its own edge spacing included. */
    if (chosenReading !== undefined) return { startOffset: zone.startOffset, endOffset: zone.endOffset, replacementText: chosenReading.writtenText };
    /** WHEN the choice empties the zone though no single reading does CONSEQUENCE its words and one space beside them go. */
    if (chosenText.length === 0) return emptiedZoneEditOf(baseText, zone);
    /**
     * FALLBACK
     *   The first reading with words lends its edge spacing; with none, the page's stretch does.
     *
     * EXAMPLE
     *   readings " Attention Legal\n", " Attention Finance\n", chosen "Attention Legal and Finance"
     *   -> " " + "Attention Legal and Finance" + "\n"
     */
    const spacingPattern = zone.options.find((option) => option.optionText.length > 0)?.writtenText ?? zoneStretch;
    return { startOffset: zone.startOffset, endOffset: zone.endOffset, replacementText: leadingSpacingOf(spacingPattern) + chosenText + trailingSpacingOf(spacingPattern) };
}

/**
 * The cards a chosen zone keeps: a card whose reading is the chosen text, or, when
 * the model joined several readings, a card whose reading stands in it as written.
 *
 * CASES
 *   - the zone keeps its words today           -> none
 *   - one or more readings are the chosen text -> those cards
 *   - "" that no reading spells                -> every card of the zone: only the union of the
 *                                                 cards' deletions empties a zone no single
 *                                                 reading empties (`settledZoneText`)
 *   - other text                               -> the cards whose reading stands in it
 *
 * Every text is compared as its words (`chosenZoneWordsOf`), so a choice handed back
 * with its own edge space keeps the card whose reading it is (§57, -26 at a5abfd4e1).
 *
 * EXAMPLE
 *   readings "forty-five (45)" (a), "ninety (90)" (b), chosen "forty-five (45) or ninety (90)" -> [a, b]
 *   reading " in full" (in-full), chosen " in full"                                         -> [in-full]
 */
function cardIdsKeptInZone(chosen: ChosenZoneText): string[] {
    const { zone } = chosen;
    const chosenText = chosenZoneWordsOf(chosen.chosenText);
    /** WHEN the chosen words are the zone's words today CONSEQUENCE no card is kept: the page's reading stands. */
    if (chosenText === chosenZoneWordsOf(zone.originalText)) return [];
    const exactReadings = zone.options.filter((option) => chosenZoneWordsOf(option.optionText) === chosenText);
    /** WHEN one or more readings are the chosen words CONSEQUENCE those cards. */
    if (exactReadings.length > 0) return exactReadings.map((option) => option.cardId);
    /** WHEN the choice empties the zone though no reading does CONSEQUENCE every card: only their deletions together empty it. */
    if (chosenText.length === 0) return zone.options.map((option) => option.cardId);
    return zone.options.filter((option) => chosenZoneWordsOf(option.optionText).length > 0 && chosenText.includes(chosenZoneWordsOf(option.optionText))).map((option) => option.cardId);
}

/**
 * A zone text read as its words: without the spacing at its edges, which belongs to
 * the gap between the zone and the words around it (`leadingSpacingOf`,
 * `trailingSpacingOf`, the edge spacing `zoneEditOf` writes from the zone's readings).
 *
 * WHY
 *   The conflict choice is a model's answer, and it may hand back a reading with its own
 *   edge space. Written between the zone's spacing it doubled the gap: card 1's " in full"
 *   chosen as " in full" went in as "  in full", and the paragraph read "…forty-five (45)
 *   days  in full." (§57, -26 at a5abfd4e1, 2026-10-19).
 *
 * EXAMPLE
 *   " in full"  -> "in full"
 *   "in full"   -> "in full"
 *   " "         -> "": a text of spacing alone holds no words
 */
function chosenZoneWordsOf(zoneText: string): string {
    const leadingSpacingLength = leadingSpacingOf(zoneText).length;
    /**
     * EXAMPLE
     *   " in full"  length 8, leading 1, trailing 0 -> slice(1, 8 - 0 = 8) = "in full"
     *   " "         length 1, leading 1, trailing 1 -> slice(1, 0) = "": the start passes the end
     */
    return zoneText.slice(leadingSpacingLength, zoneText.length - trailingSpacingOf(zoneText).length);
}

/**
 * True when two edits would write over the same characters, or one inserts at a
 * point the other writes over or touches, so only one of them can be written.
 *
 * EXAMPLE
 *   [10,12) and [11,15) -> true
 *   [10,12) and [12,15) -> false: they meet
 *   [12,12) and [12,15) -> true: the insertion sits at the other's edge
 */
function editsCollide(left: PlannedEdit, right: PlannedEdit): boolean {
    const eitherInsertsAtAPoint = left.startOffset === left.endOffset || right.startOffset === right.endOffset;
    /**
     * CASES
     *   - one is a point -> a shared edge collides: two writes at one point have no order
     *   - both cover     -> only shared characters collide
     */
    if (eitherInsertsAtAPoint) return left.startOffset <= right.endOffset && right.startOffset <= left.endOffset;
    return left.startOffset < right.endOffset && right.startOffset < left.endOffset;
}

/**
 * The word changes and zones in reading order, an edit that reaches back over the
 * previous one's characters cut back to where that one ends.
 *
 * WHY only spacing is ever shared
 *   Runs of different cards that share a word are zoned, so two edits written here
 *   cover different words; each reaches past its words only into the spacing it
 *   changed beside them. When two cards changed the same space, the earlier edit in
 *   reading order keeps it, and the later one starts at its own words.
 *
 * EXAMPLE
 *   edit a [10,16) "Road\n" (a changed the space after "Street"), edit b [15,22)
 *   "\tLondon" (b changed it too) -> b is cut to [16,22) "London"
 */
function editsInReadingOrder(edits: PlannedEdit[]): PlannedEdit[] {
    const placed: PlannedEdit[] = [];
    for (const edit of [...edits].sort((left, right) => left.startOffset - right.startOffset || left.endOffset - right.endOffset)) {
        const previous = placed[placed.length - 1];
        /** WHEN the edit starts inside the previous one's characters CONSEQUENCE it starts where that one ends, without the spacing it wrote there. */
        if (previous !== undefined && edit.startOffset < previous.endOffset) {
            placed.push({ startOffset: previous.endOffset, endOffset: Math.max(previous.endOffset, edit.endOffset), replacementText: edit.replacementText.replace(/^\s+/u, "") });
            continue;
        }
        placed.push(edit);
    }
    return placed;
}

/**
 * `text` with every edit written over it, from the last to the first so each write
 * leaves the offsets of the ones before it as they were.
 *
 * EXAMPLE
 *   "pay within thirty (30) days", [11,22) -> "sixty (60)"   -> "pay within sixty (60) days"
 */
function textWithEdits(text: string, edits: PlannedEdit[]): string {
    return [...edits]
        .sort((left, right) => right.startOffset - left.startOffset || right.endOffset - left.endOffset)
        .reduce((written, edit) => written.slice(0, edit.startOffset) + edit.replacementText + written.slice(edit.endOffset), text);
}

/**
 * The paragraph with every uncontested change written as its card wrote it and
 * every zone replaced by the text chosen for it, in the characters the cards and
 * the page hold: nothing is joined back with " ".
 *
 * CASES, for a card's change of spacing alone (`changeWritesOnlySpacing`)
 *   - the card writes words here: an uncontested run, or a zone keeps it  -> written: spacing
 *                                                                           rides along with a
 *                                                                           word change
 *   - the card's words are all in zones and none keeps it                 -> left out: the card
 *                                                                           writes nothing
 *   - a word change or a zone writes over the same characters             -> left out: the words
 *                                                                           carry their own spacing
 *
 * EXAMPLE
 *   text "Customer shall pay within thirty (30) days of receipt."
 *   zone [4,6) "thirty (30)" chosen "forty-five (45)"
 *   uncontested: insert "undisputed" at 3
 *   -> "Customer shall pay undisputed within forty-five (45) days of receipt."
 *
 * EXAMPLE, a line break that rides along
 *   text "Acme Ltd, 1 Main Street London EC1A 1AA"
 *   postcode card "…Street\nLondon EC1A 1BB" (uncontested), zone "Holdings" chosen
 *   -> "Acme Holdings Ltd, 1 Main Street\nLondon EC1A 1BB"
 */
export function composeParagraphText(baseText: string, uncontestedChanges: UncontestedCardChange[], chosenZoneTexts: ChosenZoneText[]): string {
    const uncontestedWordChanges = uncontestedChanges.filter((uncontested) => !changeWritesOnlySpacing(uncontested.change));
    const cardIdsWritingWords = new Set([...uncontestedWordChanges.map((uncontested) => uncontested.cardId), ...chosenZoneTexts.flatMap(cardIdsKeptInZone)]);
    const cardIdsInZones = new Set(chosenZoneTexts.flatMap((chosen) => chosen.zone.options.map((option) => option.cardId)));
    const wordEdits = editsInReadingOrder([...chosenZoneTexts.map((chosen) => zoneEditOf(baseText, chosen)), ...uncontestedWordChanges.map((uncontested) => uncontested.change)]);
    const spacingEdits: PlannedEdit[] = [];
    for (const uncontested of uncontestedChanges.filter((candidate) => changeWritesOnlySpacing(candidate.change))) {
        /** A card in zones with no word written anywhere wrote nothing, so its spacing is not written either. */
        const cardWritesHere = cardIdsWritingWords.has(uncontested.cardId) || !cardIdsInZones.has(uncontested.cardId);
        /** WHEN words, or an earlier card's spacing, are written over the same characters CONSEQUENCE those stand. */
        const characterAlreadyWritten = [...wordEdits, ...spacingEdits].some((placed) => editsCollide(placed, uncontested.change));
        if (cardWritesHere && !characterAlreadyWritten) spacingEdits.push(uncontested.change);
    }
    return textWithEdits(baseText, [...wordEdits, ...spacingEdits]);
}

/**
 * Whether a card's change is in a paragraph as it now reads.
 *
 *   - applied      -> every run of the card is there
 *   - partial      -> some runs are there, some are not
 *   - not_applied  -> none is
 */
export type CardLanding = "applied" | "partial" | "not_applied";

/**
 * A word as the landing check compares it: the tokenizer's content key, lower case
 * with the punctuation at its ends set aside, so a comma the model moved while
 * joining two changes does not read as the change missing.
 *
 * EXAMPLE
 *   "(45)," -> "45"
 *   "。"    -> "": a unit that is only punctuation
 */
const comparableWord = (word: string): string => WordTokenizer.contentKey(word);

/** True when `wantedWords` stand one after another somewhere in `segmentWords`, compared as `comparableWord`. */
function containsWordsInOrder(segmentWords: string[], wantedWords: string[]): boolean {
    /**
     * A word that is only punctuation compares as "" and is set aside on both sides alike.
     *
     * EXAMPLE
     *   "Milestone 1 – Kickoff" -> ["milestone", "1", "kickoff"] on both sides: the dash dropped
     *   from one side only left ["milestone", "1", "", "kickoff"] to hold ["milestone", "1",
     *   "kickoff"], and a cell never matched its own text
     */
    const comparableSegment = segmentWords.map(comparableWord).filter((word) => word.length > 0);
    const comparableWanted = wantedWords.map(comparableWord).filter((word) => word.length > 0);
    if (comparableWanted.length === 0) return true;
    /** The last start that leaves room for every wanted word is `segment length - wanted length`, so the loop stops there, inclusive. */
    for (let segmentStart = 0; segmentStart <= comparableSegment.length - comparableWanted.length; segmentStart += 1) {
        if (comparableWanted.every((word, offset) => comparableSegment[segmentStart + offset] === word)) return true;
    }
    return false;
}

/**
 * True when every word of `wantedText` stands, one after another, in
 * `containingText`, compared as `comparableWord` in the redline diff's units: the
 * check the landing audit makes for a paragraph a card inserted, a comment it
 * added, and a table cell it filled.
 *
 * EXAMPLE
 *   wantedText     "Late payments accrue interest."
 *   containingText "(e) Late payments accrue interest"
 *   -> true: the words stand in order; the list label and the full stop are set aside
 *
 *   wantedText     "修理は無償とする。"
 *   containingText "第5条修理は無償とする。"
 *   -> true: 修理 / は / 無償 / と / する stand in order after 第 / 5 / 条
 *
 * EDGE CASE
 *   `wantedText` with no word -> true: nothing was asked for.
 */
export function wordsStandInText(wantedText: string, containingText: string): boolean {
    return containsWordsInOrder(unitTextsOf(unitsOf(containingText)), unitTextsOf(unitsOf(wantedText)));
}

/**
 * How many words two texts do not share once lined up word for word: the words of
 * either that the shortest edit script between them (`wordDiff`) leaves unpaired,
 * each side given as the keys its words are compared by.
 *
 * EXAMPLE
 *   "pay within thirty (30) days"  against  "pay within sixty (60) days"
 *   -> 5 + 5 words, 3 paired ("pay", "within", "days"): `10 - 2 * 3` = 4
 */
function wordsNotSharedBetween(leftWordKeys: string[], rightWordKeys: string[]): number {
    let pairedWordCount = 0;
    for (const hunk of wordDiff(leftWordKeys, rightWordKeys)) {
        /** An equal hunk pairs `beforeEnd - beforeStart` words of each side. */
        if (hunk.tag === "equal") pairedWordCount += hunk.beforeEnd - hunk.beforeStart;
    }
    return leftWordKeys.length + rightWordKeys.length - 2 * pairedWordCount;
}

/**
 * A text's words as the landing check reads a card's words (`comparableWord`): lower
 * case, the punctuation at their ends set aside, a unit that is only punctuation dropped.
 *
 * EXAMPLE
 *   "conditioned or delayed, and" -> ["conditioned", "or", "delayed", "and"]
 */
const comparableWordKeysOf = (text: string): string[] => unitTextsOf(unitsOf(text)).map(comparableWord).filter((word) => word.length > 0);

/** A text's words exactly, punctuation and case included (`WordTokenizer.comparisonKey`). */
const exactWordKeysOf = (text: string): string[] => comparisonKeysOf(unitTextsOf(unitsOf(text)));

/**
 * How much of one run of a card the final text holds.
 *
 *   - whole -> the run is written as the card writes it
 *   - part  -> some of it is: another card wrote over the rest
 *   - none  -> none of it is
 */
type RunLanding = "whole" | "part" | "none";

/**
 * How much of one run the final text holds, by what taking the run back costs it,
 * measured against the run's own size, with words read through `wordKeysOf`.
 *
 *   gain = words the final text does not share with the card's text with the run
 *          taken back, less the words it does not share with the card's text
 *   size = words the card's text and its taken-back text do not share: the run itself
 *
 * CASES
 *   - size 0          -> null: read this way, the run's two texts are the same words
 *   - gain = size     -> whole: the final text reads as the card's text at the run (the
 *                        gain is never larger, the two counts being distances)
 *   - 0 < gain < size -> part
 *   - gain <= 0       -> none
 *
 * EXAMPLE, another card wrote over the number
 *   card "pay within sixty (60) days", taken back "pay within thirty (30) days"
 *   final "pay within sixty (90) days": gain `4 - 2` = 2, size 4 -> part
 */
function runLandingOf(finalText: string, cardText: string, takenBackText: string, wordKeysOf: (text: string) => string[]): RunLanding | null {
    const finalWordKeys = wordKeysOf(finalText);
    const cardWordKeys = wordKeysOf(cardText);
    const takenBackWordKeys = wordKeysOf(takenBackText);
    const runSize = wordsNotSharedBetween(cardWordKeys, takenBackWordKeys);
    if (runSize === 0) return null;
    const gainOfRun = wordsNotSharedBetween(finalWordKeys, takenBackWordKeys) - wordsNotSharedBetween(finalWordKeys, cardWordKeys);
    if (gainOfRun >= runSize) return "whole";
    return gainOfRun > 0 ? "part" : "none";
}

/**
 * True when `wantedWords` stand one after another in `segmentWords`, compared by
 * `WordTokenizer.comparisonKey`: the exact word, its punctuation included, so a
 * comma a card turned into a semicolon does not read as the comma still standing.
 *
 * EDGE CASE
 *   `wantedWords` empty -> true: nothing was asked for.
 */
function holdsExactWordsInOrder(segmentWords: string[], wantedWords: string[]): boolean {
    const segmentKeys = comparisonKeysOf(segmentWords);
    const wantedKeys = comparisonKeysOf(wantedWords);
    /** The last start that leaves room for every wanted word is `segment length - wanted length`, so the loop stops there, inclusive. */
    for (let segmentStart = 0; segmentStart <= segmentKeys.length - wantedKeys.length; segmentStart += 1) {
        if (wantedKeys.every((wantedKey, offset) => segmentKeys[segmentStart + offset] === wantedKey)) return true;
    }
    return false;
}

/** The card's words, the final text's words, and where each card word that still stands sits in the final text, for `runWordsStandInTheirPlace`. */
interface CardWordsInFinalText {
    cardWords: string[];
    wordsWhenProposed: string[];
    finalWords: string[];
    finalIndexOfStandingCardWord: Map<number, number>;
}

/**
 * The card's words lined up with the final text's by the redline's own diff: each card
 * word an equal hunk pairs, by the index of the final word it pairs with.
 *
 * EXAMPLE
 *   card "pay within sixty (60) days", final "pay within sixty (90) days"
 *   -> { 0: 0, 1: 1, 2: 2, 4: 4 }: "(60)" stands nowhere
 */
function cardWordsInFinalTextOf(cardParagraph: CardParagraphTexts, finalText: string): CardWordsInFinalText {
    const cardWords = unitTextsOf(unitsOf(cardParagraph.textProposed));
    const finalWords = unitTextsOf(unitsOf(finalText));
    const finalIndexOfStandingCardWord = new Map<number, number>();
    for (const hunk of wordDiff(comparisonKeysOf(cardWords), comparisonKeysOf(finalWords))) {
        if (hunk.tag !== "equal") continue;
        /** An equal hunk pairs `beforeStart + offset` with `afterStart + offset` for every offset inside it. */
        for (let offset = 0; offset < hunk.beforeEnd - hunk.beforeStart; offset += 1) finalIndexOfStandingCardWord.set(hunk.beforeStart + offset, hunk.afterStart + offset);
    }
    return { cardWords, wordsWhenProposed: unitTextsOf(unitsOf(cardParagraph.textWhenProposed)), finalWords, finalIndexOfStandingCardWord };
}

/**
 * Whether a run some of which is written (`runLandingOf` answered part) stands whole in
 * its place: the final words between the nearest card words on either side of the run
 * that still stand hold the run's words, and not the words it replaced or deleted.
 *
 * WHY a place is read only for a run part written
 *   Counted alone, the words a final text lost elsewhere can pair with the run's own
 *   words in the taken-back text and lower its gain: on the half of 7.4 a split left,
 *   "of the relevant fees" paired with the "of" of the split-off "ownership of its"
 *   (R22, 2026-10-16), and the run read part written. Its place holds it whole. A run
 *   none of which is written never comes here, so the moved words a place misreads
 *   cannot make an unwritten run read written.
 *
 * CASES, for the place
 *   - a card word on the left stands  -> it starts one past that word's final position, else at 0
 *   - a card word on the right stands -> it stops before that word's final position, else at the end
 *   - the run's words are not in it                            -> no
 *   - the run's own words hold the words it replaced, in order -> yes: their presence says nothing
 *   - the words it replaced or deleted stand in it             -> no
 *
 * EXAMPLE
 *   card "pay within sixty (60) days", run [2,4), final "pay within sixty (90) days"
 *   left "within" -> final 1, place starts at `1 + 1` = 2; right "days" -> final 4
 *   -> place ["sixty", "(90)"] lacks "(60)" -> no
 */
function runWordsStandInTheirPlace(wordEdit: PlannedTextEdit, cardWordsInFinalText: CardWordsInFinalText): boolean {
    const { cardWords, wordsWhenProposed, finalWords, finalIndexOfStandingCardWord } = cardWordsInFinalText;
    /** The card's word just before the run, `requestUnitStart - 1`, walked left past every word that no longer stands, to -1 when none does. */
    let leftStandingCardWordIndex = wordEdit.requestUnitStart - 1;
    while (leftStandingCardWordIndex >= 0 && !finalIndexOfStandingCardWord.has(leftStandingCardWordIndex)) leftStandingCardWordIndex -= 1;
    /** The card's word just after the run, `requestUnitEnd` of the half-open run, walked right to the card's end when none stands. */
    let rightStandingCardWordIndex = wordEdit.requestUnitEnd;
    while (rightStandingCardWordIndex < cardWords.length && !finalIndexOfStandingCardWord.has(rightStandingCardWordIndex)) rightStandingCardWordIndex += 1;
    /** The place's two CASES above: one past the left word's final position or 0, up to the right word's final position or the end. */
    const placeStart = leftStandingCardWordIndex >= 0 ? (finalIndexOfStandingCardWord.get(leftStandingCardWordIndex) ?? -1) + 1 : 0;
    const placeEnd = rightStandingCardWordIndex < cardWords.length ? (finalIndexOfStandingCardWord.get(rightStandingCardWordIndex) ?? finalWords.length) : finalWords.length;
    const wordsInPlace = finalWords.slice(placeStart, placeEnd);
    /** Both runs are half-open, so each slice takes exactly the edit's units on its own side. */
    const runWords = cardWords.slice(wordEdit.requestUnitStart, wordEdit.requestUnitEnd);
    const replacedWords = wordsWhenProposed.slice(wordEdit.sourceUnitStart, wordEdit.sourceUnitEnd);
    if (!containsWordsInOrder(wordsInPlace, runWords)) return false;
    if (holdsExactWordsInOrder(runWords, replacedWords)) return true;
    return !holdsExactWordsInOrder(wordsInPlace, replacedWords);
}

/**
 * The card's text with one of its runs taken back: the text it was proposed on with
 * every edit of the card's plan (`TextEditPlanner.editsBetween`) written but that one,
 * so the run's characters read as before and every other run as the card writes it.
 *
 * WHY characters, not words
 *   The plan writes some runs over characters a word shares with its neighbour: it
 *   takes "goods goods." to "goods." by striking " goods" and keeping the full stop,
 *   which word by word reads as striking "goods.". Taken back word by word, that run
 *   gave "goods. goods.", a text the paragraph never read; taken back by its own
 *   characters, it gives "goods goods." again.
 *
 * EXAMPLE
 *   textWhenProposed "pay within thirty (30) days"
 *   card             "pay promptly within sixty (60) days"
 *   plan             [3,3) -> " promptly", [11,22) -> "sixty (60)"
 *   [11,22) taken back -> "pay promptly within thirty (30) days"
 */
function cardTextWithEditTakenBack(textWhenProposed: string, plannedEdits: PlannedTextEdit[], takenBackEdit: PlannedTextEdit): string {
    return textWithEdits(textWhenProposed, plannedEdits.filter((plannedEdit) => plannedEdit !== takenBackEdit));
}

/**
 * Whether one card paragraph's change is in `finalText`, the paragraph as it reads
 * after an apply (Word's accepted text).
 *
 * Each run of words of the card's plan (`TextEditPlanner.editsBetween`, its "words"
 * edits) is judged on its own, against the card's text with that run taken back
 * (`cardTextWithEditTakenBack`). The two texts differ only at that run, so how much
 * closer the final text is to the card's text says how much of that run alone is
 * written (`runLandingOf`): all of its size, some, or none. A run is read by its words
 * as the landing check reads them, so a comma another card's join moved does not read
 * as the run missing; a run that changes only punctuation or case is read exactly.
 *
 * WHY a count of words not shared, not a place found by lining the texts up
 *   Lined up by a diff, a word the paragraph repeats pairs with whichever copy the diff
 *   prefers: the old place of a moved word, or a copy among the card's own new words.
 *   Judged at a place found that way, a card whose change stood whole read "Failed to
 *   apply" (R22, 2026-10-16: 7.4's "of the relevant fees" against the "of" in
 *   "ownership of its"), and a card that moves words read "Applied" with nothing
 *   written ("days of receipt of the invoice" -> "days receipt of of the invoice").
 *   A count is the same whichever copies a diff pairs, since the shortest edit script
 *   has one length. A final text that still reads as before is closer to every run
 *   taken back than to the card, and the card's own text is closer to the card; a move
 *   is two runs, a deletion and an insertion, and lands only when both do.
 *
 * WHY the gain is measured against the run's size
 *   Read as closer or not, a run half written by another card counted as written:
 *   "thirty (30)" -> "sixty (60)" read Applied on "sixty (90)", the card's number never
 *   in the document (R22 review, 2026-10-16).
 *
 * EXAMPLE
 *   textWhenProposed "pay within thirty (30) days"
 *   card 90          "pay within ninety (90) days"
 *   finalText        "pay within forty-five (45) days"
 *   -> the run "ninety (90)": 4 words not shared with the card, 4 with it taken back -> not_applied
 *
 * EXAMPLE, a move with only its deletion written
 *   textWhenProposed "The Supplier shall, at its cost, repair the goods."
 *   card             "The Supplier shall repair the goods at its cost."
 *   finalText        "The Supplier shall repair the goods."
 *   -> the deletion lands (its taken-back text is further away), the insertion does not -> partial
 *
 * CASES
 *   - the card changes nothing in this paragraph -> applied: nothing was asked of it
 *   - every run whole                            -> applied
 *   - every run none                             -> not_applied
 *   - anything else                              -> partial
 */
export function cardLandingOnParagraphText(cardParagraph: CardParagraphTexts, finalText: string): CardLanding {
    const plannedEdits = TextEditPlanner.editsBetween(cardParagraph.textWhenProposed, cardParagraph.textProposed);
    const wordEdits = plannedEdits.filter((plannedEdit) => plannedEdit.kind === "words");
    if (wordEdits.length === 0) return "applied";
    const cardWordsInFinalText = cardWordsInFinalTextOf(cardParagraph, finalText);
    const runLandings = wordEdits.map((wordEdit): RunLanding => {
        const takenBackText = cardTextWithEditTakenBack(cardParagraph.textWhenProposed, plannedEdits, wordEdit);
        /**
         * CASES, in order
         *   - the run changes words               -> read by its words
         *   - it changes only punctuation or case -> read exactly ("office," -> "office;")
         *   - it changes nothing read either way  -> whole: nothing tells its two texts apart
         */
        const runLandingByCount = runLandingOf(finalText, cardParagraph.textProposed, takenBackText, comparableWordKeysOf)
            ?? runLandingOf(finalText, cardParagraph.textProposed, takenBackText, exactWordKeysOf)
            ?? "whole";
        /** WHEN some of the run is written CONSEQUENCE its place says whether all of it is (`runWordsStandInTheirPlace`). */
        if (runLandingByCount !== "part") return runLandingByCount;
        return runWordsStandInTheirPlace(wordEdit, cardWordsInFinalText) ? "whole" : "part";
    });
    if (runLandings.every((runLanding) => runLanding === "whole")) return "applied";
    return runLandings.every((runLanding) => runLanding === "none") ? "not_applied" : "partial";
}

/**
 * One card's landing over all its paragraphs.
 *
 * CASES
 *   - every paragraph applied      -> applied
 *   - every paragraph not_applied  -> not_applied
 *   - anything in between          -> partial
 *   - no paragraph to read         -> applied: the card changed no bookmarked paragraph
 */
export function combinedCardLanding(paragraphLandings: CardLanding[]): CardLanding {
    if (paragraphLandings.every((landing) => landing === "applied")) return "applied";
    if (paragraphLandings.every((landing) => landing === "not_applied")) return "not_applied";
    return "partial";
}

/**
 * One paragraph where pending cards collide, as the user resolves it: the
 * paragraph's bookmark, the text every card's runs are laid on (the first card's
 * `textWhenProposed`), every card that changes the paragraph with its changes, the
 * zoning, and the zones a choice is needed for, by position in `zoning.zones`.
 *
 * `cardIds` lists every card that changes the paragraph, colliding or not: the
 * resolution writes the whole paragraph, so each of them is in it.
 */
export interface CardConflictGroup {
    bookmark: string;
    baseText: string;
    cardIds: string[];
    cardsChanges: CardChangesOnParagraph[];
    zoning: ParagraphZoning;
    openZoneIndexes: number[];
}

/**
 * Every paragraph where two or more pending cards collide on words only one reading
 * can hold, in the order the paragraphs are first named by the cards.
 *
 * CASES, per paragraph two or more cards change
 *   - a zone needs a choice                       -> a group
 *   - every zone settles alone (the same change,
 *     overlapping deletions) or none exists       -> no group: Apply All lands them as they are
 *
 * EXAMPLE
 *   cards 1 (45) and 2 (90) on PAY, card 3 on TERM
 *   -> [{ bookmark PAY, cardIds [1, 2], openZoneIndexes [0] }]
 *
 *   card 4 strikes row 2, card 5 fills row 2's empty answer cell ANS2
 *   -> [{ bookmark ANS2, cardIds [4, 5], openZoneIndexes [0] }]: the whole cell is the zone
 */
export function conflictGroupsOfCards(cards: CollisionCard[]): CardConflictGroup[] {
    const bookmarksInOrder = Array.from(new Set(cards.flatMap((card) => card.paragraphs.flatMap((paragraph) => (paragraph.bookmark && contestsParagraph(paragraph) ? [paragraph.bookmark] : [])))));
    return bookmarksInOrder.flatMap((bookmark) => {
        const namingCards = cards.flatMap((card) => {
            const paragraph = card.paragraphs.find((candidate) => candidate.bookmark === bookmark && contestsParagraph(candidate));
            return paragraph === undefined ? [] : [{ cardId: card.cardId, paragraph }];
        });
        /** A paragraph only one card changes has nothing to collide with. */
        if (namingCards.length < 2) return [];
        const baseText = namingCards[0].paragraph.textWhenProposed;
        const cardsChanges = namingCards.map(({ cardId, paragraph }) => ({ cardId, changes: changesOfCardOnText(paragraph, baseText) }));
        const cardIdsStrikingParagraph = new Set(namingCards.flatMap(({ cardId, paragraph }) => (strikesRowOfParagraph(paragraph) ? [cardId] : [])));
        const zoning = conflictZonesOfParagraph(baseText, cardsChanges, cardIdsStrikingParagraph);
        const openZoneIndexes = zoning.zones.flatMap((zone, zoneIndex) => (settledZoneText(zone, cardsChanges, baseText) === null ? [zoneIndex] : []));
        if (openZoneIndexes.length === 0) return [];
        return [{ bookmark, baseText, cardIds: namingCards.map(({ cardId }) => cardId), cardsChanges, zoning, openZoneIndexes }];
    });
}
